import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';

export interface TenantFeeConfig {
  id: string;
  tenantId: string;
  adhesionFee: number;
  adhesionFeeEnabled: boolean;
  cashInPercentage: number;
  cashInFeeEnabled: boolean;
  cashOutPercentage: number;
  cashOutFeeEnabled: boolean;
  fintechPercentage: number;
  processorPercentage: number;
  membershipFeeExemptTypes: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UpdateTenantFeeConfigDto {
  adhesionFee?: number;
  adhesionFeeEnabled?: boolean;
  cashInPercentage?: number;
  cashInFeeEnabled?: boolean;
  cashOutPercentage?: number;
  cashOutFeeEnabled?: boolean;
  fintechPercentage?: number;
  processorPercentage?: number;
  membershipFeeExemptTypes?: string[];
}

export interface FeeCalculationResult {
  grossAmount: number;
  adhesionFee: number;
  cashInFee: number;
  totalFees: number;
  netAmount: number;
  breakdown: {
    adhesionFeeApplied: boolean;
    cashInFeeApplied: boolean;
    cashInRate: number;
  };
}

export function useTenantFeeConfig(tenantId?: string) {
  return useQuery<TenantFeeConfig>({
    queryKey: ['tenant-fee-config', tenantId],
    queryFn: async () => {
      const { data } = await api.get(ENDPOINTS.tenantFeeConfig.get(tenantId!));
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useUpdateTenantFeeConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, dto }: { tenantId: string; dto: UpdateTenantFeeConfigDto }) => {
      const { data } = await api.put(ENDPOINTS.tenantFeeConfig.update(tenantId), dto);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-fee-config', variables.tenantId] });
    },
  });
}

export function useCalculateFees() {
  return useMutation<FeeCalculationResult, Error, { tenantId: string; grossAmountCentavos: number }>({
    mutationFn: async ({ tenantId, grossAmountCentavos }) => {
      const { data } = await api.post(ENDPOINTS.tenantFeeConfig.calculate, {
        tenantId,
        grossAmountCentavos,
      });
      return data;
    },
  });
}
